// ═══════════════════════════════════════════════════════════════
// useTemplateVariables – az aktív szervezet sablonváltozói (megnevezés, kulcs, érték)
// Egy közös gyorsítótár: több mező / beszúró gomb is ugyanazt a választ használja;
// mentés után a `invalidateTemplateVariables()` frissíti mindegyiket.
// ═══════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { ApiClient } from '../api/client';
import type { TemplateDefinition } from '../types';
import { useAuth } from '../context/AuthContext';

export interface TemplateVariableItem {
  key: string;
  label: string;
  value: string;
  category: string;
  custom?: boolean;
  computed?: boolean;
  /** tartalom-változónál: a formázott (HTML) tartalom, a szervezet adataival kitöltve */
  html?: string;
}

type Entry = { tenantKey: string; promise: Promise<TemplateDefinition | null>; data?: TemplateDefinition | null };
let cache: Entry | null = null;
const listeners = new Set<() => void>();

function load(tenantKey: string, tenantId?: number) {
  if (cache && cache.tenantKey === tenantKey) return cache.promise;
  const entry: Entry = {
    tenantKey,
    promise: ApiClient.getTemplateDefinition(tenantId).then((res) => {
      const def = res.ok ? ((res.data ?? res) as TemplateDefinition) : null;
      entry.data = def && def.suggestedVariables ? def : null;
      return entry.data;
    }),
  };
  cache = entry;
  return entry.promise;
}

export function invalidateTemplateVariables() {
  cache = null;
  listeners.forEach((fn) => fn());
}

export function toVariableItems(def: TemplateDefinition | null | undefined): TemplateVariableItem[] {
  if (!def) return [];
  const vals = def.suggestedVariables || {};
  return (def.variableDefinitions || []).map((d) => ({
    key: d.key,
    label: d.label,
    value: String(vals[d.key] ?? ''),
    category: d.category,
    custom: d.custom,
    computed: d.computed,
  }));
}

export function useTemplateVariables(tenantId?: number, enabled = true) {
  // a gyorsítótár kulcsa a TÉNYLEGES szervezet: megszemélyesítéskor (átlépéskor) és
  // visszalépéskor az aktív szervezet változik → új lekérés, nem a korábbi szervezet adatai
  const { tenant } = useAuth();
  const tenantKey = tenantId ? `t${tenantId}` : `aktiv-${tenant?.id ?? ''}`;
  const [def, setDef] = useState<TemplateDefinition | null>(cache?.tenantKey === tenantKey ? cache.data ?? null : null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    // szervezetváltáskor a régi szervezet értékei egy pillanatra se látszódjanak
    setDef(cache?.tenantKey === tenantKey ? cache.data ?? null : null);
    setLoading(true);
    load(tenantKey, tenantId).then((d) => { if (alive) { setDef(d); setLoading(false); } });
    return () => { alive = false; };
  }, [tenantKey, tenantId, enabled, tick]);

  const reload = useCallback(() => invalidateTemplateVariables(), []);
  return { definition: def, items: toVariableItems(def), loading, reload, canSave: Boolean(def?.canSaveVariables) };
}
